import {Component, inject} from '@angular/core';
import {BaseModalComponent} from "../../base-modal/base-modal.component";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {FilesAccordionStateService} from "../services/files-accordion-state.service";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {FilesGenerateDocumentService} from "../services/files-generate-document.service";
import {
  AttachFileParams,
  AttachFileToRecord,
  FilesViewerData,
  FileTypeName,
  GenerateFileTypeName,
  OpenFilesViewerModalParams,
  RecordCalendarFileParams
} from "../../../../types/file.types";
import {generateFileIdKeys} from "../../documents-modal/configs/documents-generate-modal-config";
import {RecordCalendarFilesService} from "../../../../services/record-calendar-files.service";
import {MultiFilesParamsService} from "../services/multi-files-params.service";
import {FileCardComponent} from "../../../../../shared/components/ui/file-card/file-card.component";
import {SingleFilesParamsService} from "../services/single-files-params.service";
import {FileUploadCardComponent} from "../../../../../shared/components/ui/file-upload-card/file-upload-card.component";
import {multiTypeFilesViewerModalName} from "../configs/multi-type-files-viewer-config";
import {CurrentUserRoleAndLogin} from "../../../../types/auth.types";
import {LoaderComponent} from "../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-multi-type-files-viewer-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    FileCardComponent,
    FileUploadCardComponent,
    LoaderComponent
  ],
  templateUrl: './multi-type-files-viewer-modal.component.html',
  styleUrl: './multi-type-files-viewer-modal.component.scss'
})
export class MultiTypeFilesViewerModalComponent extends BaseModalComponent {
  public dialogData = inject<OpenFilesViewerModalParams>(MAT_DIALOG_DATA);
  public multiFilesParamsService: MultiFilesParamsService = inject(MultiFilesParamsService);
  private filesAccordionStateService: FilesAccordionStateService = inject(FilesAccordionStateService);
  private filesGenerateDocumentService: FilesGenerateDocumentService = inject(FilesGenerateDocumentService);
  private recordCalendarFilesServiceService: RecordCalendarFilesService = inject(RecordCalendarFilesService);
  private recordCalendarFilesService: RecordCalendarFilesService = inject(RecordCalendarFilesService);
  private singleFilesParamsService: SingleFilesParamsService = inject(SingleFilesParamsService);

  public loginAndRoles: CurrentUserRoleAndLogin | null = null;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.modalStatesService.openModal(multiTypeFilesViewerModalName);
    this.loginAndRoles = this.authService.getRolesAndLogin();
  }

  public override ngOnDestroy(): void {
    this.modalStatesService.closeModal(multiTypeFilesViewerModalName);
    this.editModeService.setEditMode(this.dialogData.isEditMode || false);
    this.multiFilesParamsService.resetMultiFileParams();
  }

  public accordionToggle(key: string): void {
    this.filesAccordionStateService.togglePanel({ key });
  }

  public getAccordionTitle(accordion: FilesViewerData): string {
    const count = this.multiFilesParamsService.multiFileParams()?.[accordion.key]?.length || '-';

    return `${accordion.title} (${count})`;
  }

  public async handleGenerate(key: FileTypeName): Promise<void> {
    if (!generateFileIdKeys.includes(key)) { return; }

    this.loadingService.setIsLoadingState(true);
    const generateFn = this.filesGenerateDocumentService.generateMethodMap[key as GenerateFileTypeName];

    if (!generateFn) {
      this.errorService.showErrorMessage('Неизвестный тип генерации');
      return;
    }

    const file = await generateFn(this.dialogData.recordId).catch(() => {
      this.loadingService.setIsLoadingState(false);
      this.closeDialog('cancel');
      return null;
    });

    if (!file) { return; }

    const attachFileToRecord: AttachFileToRecord = {
      recordCalendarId: this.dialogData.recordCalendarId,
      fileId: file.id,
      type: key
    };

    await this.recordCalendarFilesServiceService.attachFileToRecord(attachFileToRecord)
      .then((recordCalendarFileParams) => this.multiFilesParamsService.updateMultiFileParams(key, recordCalendarFileParams));

    this.loadingService.setIsLoadingState(false);
  }

  public async handleDelete(isImage: boolean, recordCalendarFileParams: RecordCalendarFileParams): Promise<void> {
    this.recordCalendarFilesService.deleteFileToRecord(recordCalendarFileParams.id)
      .then(() => {
        this.dialogData.isAllFiles ?
          this.multiFilesParamsService.deleteMultiFileParams(recordCalendarFileParams.type, recordCalendarFileParams.id) :
          this.singleFilesParamsService.deleteFilesData(recordCalendarFileParams.id);
      });
  }

  public async attachFile({ parentId, fileId, type }: AttachFileParams): Promise<void> {
    const attachFileToRecord: AttachFileToRecord = {
      recordCalendarId: parentId,
      fileId: fileId,
      type: type
    };

    await this.recordCalendarFilesServiceService.attachFileToRecord(attachFileToRecord)
      .then((recordCalendarFileParams) => this.multiFilesParamsService.updateMultiFileParams(type, recordCalendarFileParams));
  }

  public getEditMode(): boolean {
    if (this.loginAndRoles?.role === 'ROLE_PROMOTION_SPECIALIST') { return false; }

    return this.isEditModeActive;
  }
}
